import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useUrlScraper } from "@/hooks/useUrlScraper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  FileText, 
  ArrowRight, 
  ArrowLeft, 
  Lightbulb, 
  Check, 
  Loader2,
  Building2,
  Eye,
  Target,
  MessageSquare,
  Sparkles,
  Plus,
  RotateCcw,
  Globe,
  Search,
  CheckCircle2,
  AlertCircle,
  Wand2,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { toast } from "sonner";
import LeadershipPlanCanvas, { LeadershipPlan } from "@/components/LeadershipPlanCanvas";

interface WizardData {
  // Step 1: Basic Info
  dioceseName: string;
  dioceseUrl: string;
  zipCode: string;
  city: string;
  state: string;
  bishopName: string;
  dioceseSize: string;
  
  // Suggested from website (Step 1)
  suggestedDioceseName: string;
  suggestedBishopName: string;
  suggestedCity: string;
  suggestedState: string;
  suggestedZipCode: string;
  suggestedDioceseSize: string;
  
  // Step 2: Vision & Mission
  visionStatement: string;
  missionStatement: string;
  suggestedVision: string;
  suggestedMission: string;
  suggestionSource: 'scraped' | 'generated' | null;
  
  // Step 3: Key Priorities (sections to include)
  sections: {
    visionMission: boolean;
    spiritualFormation: boolean;
    evangelization: boolean;
    catholicEducation: boolean;
    charitableOutreach: boolean;
    administrativeExcellence: boolean;
    communication: boolean;
  };
  useDefaults: {
    spiritualFormation: boolean;
    evangelization: boolean;
    catholicEducation: boolean;
    charitableOutreach: boolean;
    administrativeExcellence: boolean;
    communication: boolean;
  };
  
  // Step 4: Quick Context
  additionalContext: string;
  suggestedContext: string;
}

const DEFAULT_WIZARD_DATA: WizardData = {
  dioceseName: "",
  dioceseUrl: "",
  zipCode: "",
  city: "",
  state: "",
  bishopName: "",
  dioceseSize: "",
  suggestedDioceseName: "",
  suggestedBishopName: "",
  suggestedCity: "",
  suggestedState: "",
  suggestedZipCode: "",
  suggestedDioceseSize: "",
  visionStatement: "",
  missionStatement: "",
  suggestedVision: "",
  suggestedMission: "",
  suggestionSource: null,
  sections: {
    visionMission: true,
    spiritualFormation: true,
    evangelization: true,
    catholicEducation: true,
    charitableOutreach: true,
    administrativeExcellence: true,
    communication: true,
  },
  useDefaults: {
    spiritualFormation: true,
    evangelization: true,
    catholicEducation: true,
    charitableOutreach: true,
    administrativeExcellence: true,
    communication: true,
  },
  additionalContext: "",
  suggestedContext: "",
};

const SECTION_LABELS: Record<string, { title: string; description: string }> = {
  visionMission: { 
    title: "Vision and Mission Statement", 
    description: "Core purpose and direction of the diocese" 
  },
  spiritualFormation: { 
    title: "Spiritual Formation and Pastoral Life", 
    description: "Sacramental life, vocations, and spiritual growth" 
  },
  evangelization: { 
    title: "Evangelization and Catechesis", 
    description: "Spreading the Gospel and faith formation" 
  },
  catholicEducation: { 
    title: "Catholic Education", 
    description: "Schools, religious education, and youth ministry" 
  },
  charitableOutreach: { 
    title: "Charitable Outreach and Social Services", 
    description: "Service to the poor and vulnerable" 
  },
  administrativeExcellence: { 
    title: "Administrative Excellence and Stewardship", 
    description: "Financial health, HR, and governance" 
  },
  communication: { 
    title: "Communication and Engagement", 
    description: "Internal and external communications strategy" 
  },
};

const PROGRESS_MESSAGES = [
  "Reviewing diocesan context...",
  "Consulting Church documents...",
  "Analyzing pastoral priorities...",
  "Drafting spiritual formation strategies...",
  "Compiling evangelization initiatives...",
  "Reviewing administrative best practices...",
  "Formatting your leadership plan...",
];

// Search status for the Apple/Grok-style UI
interface SearchStatus {
  isSearching: boolean;
  hostname: string;
  foundItems: string[];
  error: string | null;
  complete: boolean;
}

const DiocesanPlannerWizard: React.FC = () => {
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const { scrapeUrls, isScraping } = useUrlScraper();
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState<WizardData>(DEFAULT_WIZARD_DATA);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [lookingUpZip, setLookingUpZip] = useState(false);
  const [existingPlan, setExistingPlan] = useState<LeadershipPlan | null>(null);
  const [showCanvas, setShowCanvas] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState<LeadershipPlan | null>(null);
  
  // URL scraping state - Apple/Grok style
  const [searchStatus, setSearchStatus] = useState<SearchStatus>({
    isSearching: false,
    hostname: "",
    foundItems: [],
    error: null,
    complete: false,
  });
  const [searchExpanded, setSearchExpanded] = useState(false);
  const urlDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrapedUrlRef = useRef<string>("");

  // Scroll to top when step changes
  useEffect(() => {
    // Use requestAnimationFrame to ensure scroll happens after render
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [currentStep]);

  // Cycle through progress messages while generating
  useEffect(() => {
    if (!isGenerating) {
      setProgressMessage("");
      return;
    }
    
    let messageIndex = 0;
    setProgressMessage(PROGRESS_MESSAGES[0]);
    
    const interval = setInterval(() => {
      messageIndex = (messageIndex + 1) % PROGRESS_MESSAGES.length;
      setProgressMessage(PROGRESS_MESSAGES[messageIndex]);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isGenerating]);

  const plannerLabel = organization?.settings?.planner_label || "Diocesan Leadership Planner";
  const welcomeSubheader = organization?.settings?.planner_welcome_subheader || 
    "Create a comprehensive leadership plan for your diocese in minutes";
  const examplePrompt = organization?.settings?.planner_welcome_example ||
    "Create a 3-year strategic plan for the Diocese of Springfield, focusing on youth evangelization and parish revitalization";

  // Check for existing plan on mount
  useEffect(() => {
    const savedPlan = sessionStorage.getItem("generatedPlan");
    if (savedPlan) {
      try {
        const parsed = JSON.parse(savedPlan);
        setExistingPlan(parsed);
      } catch (e) {
        sessionStorage.removeItem("generatedPlan");
      }
    }
  }, []);

  // Listen for plan generation event
  useEffect(() => {
    const handlePlanGenerated = (event: CustomEvent<LeadershipPlan>) => {
      setGeneratedPlan(event.detail);
      setShowCanvas(true);
    };
    
    window.addEventListener("planGenerated", handlePlanGenerated as EventListener);
    return () => window.removeEventListener("planGenerated", handlePlanGenerated as EventListener);
  }, []);

  // Auto-search URL when user enters a valid URL (debounced)
  useEffect(() => {
    if (urlDebounceRef.current) {
      clearTimeout(urlDebounceRef.current);
    }

    const url = wizardData.dioceseUrl.trim();
    
    if (!url || url === lastScrapedUrlRef.current || searchStatus.isSearching) {
      return;
    }

    let isValidUrl = false;
    let hostname = "";
    try {
      const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
      isValidUrl = parsed.hostname.includes('.');
      hostname = parsed.hostname;
    } catch {
      isValidUrl = false;
    }

    if (!isValidUrl) {
      return;
    }

    urlDebounceRef.current = setTimeout(() => {
      searchDioceseUrl(url, hostname);
    }, 800);

    return () => {
      if (urlDebounceRef.current) {
        clearTimeout(urlDebounceRef.current);
      }
    };
  }, [wizardData.dioceseUrl]);

  const searchDioceseUrl = async (url: string, hostname: string) => {
    const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;
    lastScrapedUrlRef.current = wizardData.dioceseUrl.trim();
    
    setSearchStatus({
      isSearching: true,
      hostname,
      foundItems: [],
      error: null,
      complete: false,
    });
    setSearchExpanded(false);

    try {
      const resultsMap = await scrapeUrls([normalizedUrl]);
      const scrapedContent = resultsMap.get(normalizedUrl);
      
      if (!scrapedContent || !scrapedContent.success) {
        throw new Error("Could not fetch website content");
      }
      
      const { data, error } = await supabase.functions.invoke("extract-vision-mission", {
        body: {
          scrapedContent,
          organizationId: organization?.id
        }
      });
      
      if (error) throw error;
      
      // Update wizard data with all extracted info
      setWizardData(prev => {
        const updated = { ...prev };
        
        // Auto-fill empty fields
        if (data.suggestedDioceseName && !prev.dioceseName) {
          updated.dioceseName = data.suggestedDioceseName;
        }
        if (data.suggestedBishopName && !prev.bishopName) {
          updated.bishopName = data.suggestedBishopName;
        }
        if (data.suggestedCity && !prev.city) {
          updated.city = data.suggestedCity;
        }
        if (data.suggestedState && !prev.state) {
          updated.state = data.suggestedState;
        }
        if (data.suggestedZipCode && !prev.zipCode) {
          updated.zipCode = data.suggestedZipCode;
        }
        if (data.suggestedDioceseSize && !prev.dioceseSize) {
          updated.dioceseSize = data.suggestedDioceseSize;
        }
        
        // Store suggestions for later use
        updated.suggestedDioceseName = data.suggestedDioceseName || "";
        updated.suggestedBishopName = data.suggestedBishopName || "";
        updated.suggestedCity = data.suggestedCity || "";
        updated.suggestedState = data.suggestedState || "";
        updated.suggestedZipCode = data.suggestedZipCode || "";
        updated.suggestedDioceseSize = data.suggestedDioceseSize || "";
        updated.suggestedVision = data.suggestedVision || "";
        updated.suggestedMission = data.suggestedMission || "";
        updated.suggestedContext = data.suggestedContext || "";
        updated.suggestionSource = data.source;
        
        return updated;
      });
      
      setSearchStatus({
        isSearching: false,
        hostname,
        foundItems: data.foundItems || [],
        error: null,
        complete: true,
      });
      
      const itemCount = (data.foundItems || []).length;
      if (itemCount > 0) {
        toast.success(`Found ${itemCount} items from ${hostname}`);
      }
      
    } catch (error) {
      console.error("URL search error:", error);
      const errorMsg = error instanceof Error ? error.message : "Failed to search website";
      setSearchStatus({
        isSearching: false,
        hostname,
        foundItems: [],
        error: errorMsg,
        complete: true,
      });
      toast.error(errorMsg);
    }
  };

  // Reset wizard to start fresh
  const handleStartFresh = () => {
    sessionStorage.removeItem("generatedPlan");
    sessionStorage.removeItem("wizardData");
    setExistingPlan(null);
    setGeneratedPlan(null);
    setShowCanvas(false);
    setWizardData(DEFAULT_WIZARD_DATA);
    setSearchStatus({ isSearching: false, hostname: "", foundItems: [], error: null, complete: false });
    lastScrapedUrlRef.current = "";
    setCurrentStep(1);
    toast.success("Starting fresh plan");
  };

  // Continue with existing plan - show canvas
  const handleContinuePlan = () => {
    const savedWizardData = sessionStorage.getItem("wizardData");
    if (savedWizardData) {
      try {
        setWizardData(JSON.parse(savedWizardData));
      } catch (e) {
        // Use defaults if parsing fails
      }
    }
    setGeneratedPlan(existingPlan);
    setShowCanvas(true);
  };

  // Zip code lookup
  useEffect(() => {
    const lookupZip = async () => {
      if (wizardData.zipCode.length === 5 && /^\d{5}$/.test(wizardData.zipCode)) {
        setLookingUpZip(true);
        try {
          const response = await fetch(`https://api.zippopotam.us/us/${wizardData.zipCode}`);
          if (response.ok) {
            const data = await response.json();
            if (data.places?.[0]) {
              setWizardData(prev => ({
                ...prev,
                city: data.places[0]["place name"],
                state: data.places[0]["state abbreviation"]
              }));
            }
          }
        } catch (error) {
          console.error("Zip lookup failed:", error);
        } finally {
          setLookingUpZip(false);
        }
      }
    };
    lookupZip();
  }, [wizardData.zipCode]);

  // Show canvas view if we have a generated plan (MUST be after all hooks)
  if (showCanvas && generatedPlan) {
    return (
      <div className="h-[calc(100vh-64px)]">
        <LeadershipPlanCanvas 
          plan={generatedPlan} 
          onStartNew={handleStartFresh}
        />
      </div>
    );
  }

  const updateField = <K extends keyof WizardData>(field: K, value: WizardData[K]) => {
    setWizardData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSection = (section: keyof WizardData["sections"]) => {
    setWizardData(prev => ({
      ...prev,
      sections: { ...prev.sections, [section]: !prev.sections[section] }
    }));
  };

  const toggleDefault = (section: keyof WizardData["useDefaults"]) => {
    setWizardData(prev => ({
      ...prev,
      useDefaults: { ...prev.useDefaults, [section]: !prev.useDefaults[section] }
    }));
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-leadership-plan", {
        body: {
          wizardData,
          organization_id: organization?.id
        }
      });

      if (error) throw error;

      sessionStorage.setItem("generatedPlan", JSON.stringify(data));
      sessionStorage.setItem("wizardData", JSON.stringify(wizardData));
      
      window.dispatchEvent(new CustomEvent("planGenerated", { detail: data }));
      
      toast.success("Plan generated successfully!");
    } catch (error) {
      console.error("Generation error:", error);
      toast.error("Failed to generate plan. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return wizardData.dioceseName.trim() && wizardData.dioceseSize;
      case 2:
        return wizardData.visionStatement.trim() || wizardData.missionStatement.trim();
      case 3:
        return Object.values(wizardData.sections).some(v => v);
      case 4:
        return true;
      case 5:
        return true;
      default:
        return true;
    }
  };

  const progress = (currentStep / 5) * 100;

  // Check if a field was auto-filled from website
  const isFromWebsite = (field: keyof WizardData, suggestedField: keyof WizardData) => {
    return wizardData[suggestedField] && wizardData[field] === wizardData[suggestedField];
  };

  // Apple/Grok-style Search Status Component
  const SearchStatusPanel = () => {
    if (!searchStatus.isSearching && !searchStatus.complete) return null;

    return (
      <div className="rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 overflow-hidden transition-all duration-300">
        <Collapsible open={searchExpanded} onOpenChange={setSearchExpanded}>
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors">
              {/* Left: Icon */}
              <div className="flex-shrink-0">
                {searchStatus.isSearching ? (
                  <div className="w-5 h-5 relative">
                    <Search className="w-5 h-5 text-primary animate-pulse" />
                  </div>
                ) : searchStatus.error ? (
                  <AlertCircle className="w-5 h-5 text-destructive" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                )}
              </div>

              {/* Center: Status text */}
              <div className="flex-1 text-left">
                <span className="text-sm text-foreground">
                  {searchStatus.isSearching ? (
                    `Searching ${searchStatus.hostname}...`
                  ) : searchStatus.error ? (
                    `Failed to search ${searchStatus.hostname}`
                  ) : (
                    `Found ${searchStatus.foundItems.length} items from ${searchStatus.hostname}`
                  )}
                </span>
              </div>

              {/* Right: Caret */}
              {searchStatus.complete && !searchStatus.error && searchStatus.foundItems.length > 0 && (
                <div className="flex-shrink-0 text-muted-foreground">
                  {searchExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </div>
              )}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent>
            {searchStatus.foundItems.length > 0 && (
              <div className="px-3 pb-3 pt-1 border-t border-border/30">
                <div className="flex flex-wrap gap-1.5">
                  {searchStatus.foundItems.map((item, index) => (
                    <span
                      key={item}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/10 text-primary animate-in fade-in slide-in-from-bottom-1 duration-200"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  };

  // From website indicator badge
  const WebsiteBadge = () => (
    <Badge variant="secondary" className="text-[10px] h-5 gap-0.5 font-normal">
      <Globe className="w-3 h-3" />
      From website
    </Badge>
  );

  // Welcome Screen (Step 0)
  if (currentStep === 0) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-6">
        <div className="max-w-2xl w-full text-center space-y-8">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <FileText className="w-8 h-8 text-primary" />
            </div>
          </div>

          <div className="space-y-3">
            <h1 className="text-4xl font-bold font-display text-foreground">
              {plannerLabel}
            </h1>
            <p className="text-lg text-muted-foreground max-w-lg mx-auto">
              {welcomeSubheader}
            </p>
          </div>

          {existingPlan ? (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-muted/50 max-w-md mx-auto">
                <p className="text-sm text-muted-foreground mb-3">
                  You have an existing plan: <span className="font-medium text-foreground">{existingPlan.title || "Untitled Plan"}</span>
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={handleContinuePlan} size="lg">
                    <Eye className="mr-2 h-4 w-4" />
                    View Plan
                  </Button>
                  <Button onClick={handleStartFresh} variant="outline" size="lg">
                    <Plus className="mr-2 h-4 w-4" />
                    Start New Plan
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Button 
              onClick={() => setCurrentStep(1)} 
              size="lg"
              className="px-8"
            >
              Begin Planning
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          )}

          <button
            type="button"
            onClick={() => setCurrentStep(1)}
            className="group inline-flex items-start gap-3 text-left p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors max-w-lg mx-auto"
          >
            <Lightbulb className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <span className="text-sm font-medium text-foreground">Example</span>
              <p className="text-sm text-muted-foreground group-hover:text-foreground/80 transition-colors">
                "{examplePrompt}"
              </p>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Wizard Steps
  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      {/* Progress Header */}
      <div className="border-b border-border bg-card/50">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-foreground">
              {plannerLabel}
            </h2>
            <span className="text-sm text-muted-foreground">
              Step {currentStep} of 5
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <ScrollArea className="h-[calc(100vh-250px)]">
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Basic Information</h3>
                  <p className="text-sm text-muted-foreground">Tell us about your diocese</p>
                </div>
              </div>

              <div className="grid gap-4">
                {/* Diocese URL field - first to enable auto-fill */}
                <div>
                  <Label htmlFor="dioceseUrl">Diocese Website URL</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="dioceseUrl"
                      placeholder="e.g., archny.org"
                      value={wizardData.dioceseUrl}
                      onChange={(e) => updateField("dioceseUrl", e.target.value)}
                      className="pl-10"
                    />
                    {searchStatus.isSearching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-primary" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    We'll auto-fill information from your website
                  </p>
                </div>

                {/* Apple/Grok-style Search Status */}
                <SearchStatusPanel />

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label htmlFor="dioceseName">Diocese Name *</Label>
                    <div className="flex items-center gap-2">
                      {isFromWebsite("dioceseName", "suggestedDioceseName") && <WebsiteBadge />}
                      {wizardData.suggestedDioceseName && wizardData.dioceseName !== wizardData.suggestedDioceseName && (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => updateField("dioceseName", wizardData.suggestedDioceseName)}
                          className="h-auto p-0 text-xs text-primary"
                        >
                          Paste from website?
                        </Button>
                      )}
                    </div>
                  </div>
                  <Input
                    id="dioceseName"
                    placeholder="e.g., Diocese of Springfield"
                    value={wizardData.dioceseName}
                    onChange={(e) => updateField("dioceseName", e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <Label htmlFor="zipCode">Zip Code</Label>
                      <div className="flex items-center gap-2">
                        {isFromWebsite("zipCode", "suggestedZipCode") && <WebsiteBadge />}
                        {wizardData.suggestedZipCode && wizardData.zipCode !== wizardData.suggestedZipCode && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => updateField("zipCode", wizardData.suggestedZipCode)}
                            className="h-auto p-0 text-xs text-primary"
                          >
                            Paste?
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="relative">
                      <Input
                        id="zipCode"
                        placeholder="12345"
                        value={wizardData.zipCode}
                        onChange={(e) => updateField("zipCode", e.target.value.slice(0, 5))}
                        maxLength={5}
                      />
                      {lookingUpZip && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <Label htmlFor="city">City</Label>
                      {isFromWebsite("city", "suggestedCity") && <WebsiteBadge />}
                    </div>
                    <Input
                      id="city"
                      placeholder="City"
                      value={wizardData.city}
                      onChange={(e) => updateField("city", e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <Label htmlFor="state">State</Label>
                      {isFromWebsite("state", "suggestedState") && <WebsiteBadge />}
                    </div>
                    <Input
                      id="state"
                      placeholder="ST"
                      value={wizardData.state}
                      onChange={(e) => updateField("state", e.target.value.slice(0, 2).toUpperCase())}
                      maxLength={2}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label htmlFor="bishopName">Bishop's Name</Label>
                    <div className="flex items-center gap-2">
                      {isFromWebsite("bishopName", "suggestedBishopName") && <WebsiteBadge />}
                      {wizardData.suggestedBishopName && wizardData.bishopName !== wizardData.suggestedBishopName && (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => updateField("bishopName", wizardData.suggestedBishopName)}
                          className="h-auto p-0 text-xs text-primary"
                        >
                          Paste from website?
                        </Button>
                      )}
                    </div>
                  </div>
                  <Input
                    id="bishopName"
                    placeholder="e.g., Most Rev. John Smith"
                    value={wizardData.bishopName}
                    onChange={(e) => updateField("bishopName", e.target.value)}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label htmlFor="dioceseSize">Diocese Size *</Label>
                    <div className="flex items-center gap-2">
                      {isFromWebsite("dioceseSize", "suggestedDioceseSize") && <WebsiteBadge />}
                      {wizardData.suggestedDioceseSize && wizardData.dioceseSize !== wizardData.suggestedDioceseSize && (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => updateField("dioceseSize", wizardData.suggestedDioceseSize)}
                          className="h-auto p-0 text-xs text-primary"
                        >
                          Paste from website?
                        </Button>
                      )}
                    </div>
                  </div>
                  <Select 
                    value={wizardData.dioceseSize} 
                    onValueChange={(value) => updateField("dioceseSize", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small (under 100,000 Catholics)</SelectItem>
                      <SelectItem value="medium">Medium (100,000 - 500,000 Catholics)</SelectItem>
                      <SelectItem value="large">Large (500,000 - 1,000,000 Catholics)</SelectItem>
                      <SelectItem value="archdiocese">Archdiocese (over 1,000,000 Catholics)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Vision & Mission */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Vision & Mission</h3>
                  <p className="text-sm text-muted-foreground">
                    Discerned through the Bishop and Holy Spirit
                  </p>
                </div>
              </div>

              {/* Suggestions from website */}
              {(wizardData.suggestedVision || wizardData.suggestedMission) && (
                <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Wand2 className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base">Suggested from your website</CardTitle>
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {wizardData.suggestionSource === 'scraped' ? 'Found on site' : 'AI Generated'}
                      </Badge>
                    </div>
                    <CardDescription>
                      {wizardData.suggestionSource === 'scraped' 
                        ? "We found these statements on your diocese website"
                        : "Generated based on your website content"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {wizardData.suggestedVision && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Vision Statement</Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateField("visionStatement", wizardData.suggestedVision)}
                            className="h-7 text-xs"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Use this vision
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground bg-background/50 rounded-md p-3 border">
                          {wizardData.suggestedVision}
                        </p>
                      </div>
                    )}
                    {wizardData.suggestedMission && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Mission Statement</Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateField("missionStatement", wizardData.suggestedMission)}
                            className="h-7 text-xs"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Use this mission
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground bg-background/50 rounded-md p-3 border">
                          {wizardData.suggestedMission}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card className="bg-muted/30 border-dashed">
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground">
                    <strong>Note:</strong> These statements are typically discerned through prayer 
                    and consultation. The AI will only clean up formatting if needed—your words 
                    and intent will be preserved.
                  </p>
                </CardContent>
              </Card>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label htmlFor="visionStatement">Vision Statement</Label>
                    {wizardData.suggestedVision && !wizardData.visionStatement && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => updateField("visionStatement", wizardData.suggestedVision)}
                        className="h-auto p-0 text-xs text-primary"
                      >
                        Paste from diocese website?
                      </Button>
                    )}
                  </div>
                  <Textarea
                    id="visionStatement"
                    placeholder="What is the long-term vision for your diocese? What do you aspire to become?"
                    value={wizardData.visionStatement}
                    onChange={(e) => updateField("visionStatement", e.target.value)}
                    className="min-h-[120px]"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label htmlFor="missionStatement">Mission Statement</Label>
                    {wizardData.suggestedMission && !wizardData.missionStatement && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => updateField("missionStatement", wizardData.suggestedMission)}
                        className="h-auto p-0 text-xs text-primary"
                      >
                        Paste from diocese website?
                      </Button>
                    )}
                  </div>
                  <Textarea
                    id="missionStatement"
                    placeholder="What is your diocese's purpose? How do you serve God's people?"
                    value={wizardData.missionStatement}
                    onChange={(e) => updateField("missionStatement", e.target.value)}
                    className="min-h-[120px]"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Key Priorities */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Key Priorities</h3>
                  <p className="text-sm text-muted-foreground">
                    Select which sections to include in your plan
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {Object.entries(SECTION_LABELS).map(([key, { title, description }]) => {
                  const sectionKey = key as keyof WizardData["sections"];
                  const isVisionMission = key === "visionMission";
                  
                  return (
                    <Card 
                      key={key} 
                      className={`transition-colors ${
                        wizardData.sections[sectionKey] 
                          ? "border-primary/50 bg-primary/5" 
                          : "border-border bg-card"
                      } ${isVisionMission ? "opacity-75" : ""}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id={key}
                            checked={wizardData.sections[sectionKey]}
                            onCheckedChange={() => !isVisionMission && toggleSection(sectionKey)}
                            disabled={isVisionMission}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <Label 
                              htmlFor={key}
                              className={`text-sm font-medium cursor-pointer ${isVisionMission ? "cursor-not-allowed" : ""}`}
                            >
                              {title}
                              {isVisionMission && (
                                <span className="ml-2 text-xs text-muted-foreground">(Always included)</span>
                              )}
                            </Label>
                            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
                            
                            {/* Use defaults toggle for non-vision/mission sections */}
                            {!isVisionMission && wizardData.sections[sectionKey] && (
                              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
                                <Checkbox
                                  id={`default-${key}`}
                                  checked={wizardData.useDefaults[sectionKey as keyof WizardData["useDefaults"]]}
                                  onCheckedChange={() => toggleDefault(sectionKey as keyof WizardData["useDefaults"])}
                                />
                                <Label 
                                  htmlFor={`default-${key}`}
                                  className="text-xs text-muted-foreground cursor-pointer"
                                >
                                  Use AI-suggested content for this section
                                </Label>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Additional Context */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Additional Context</h3>
                  <p className="text-sm text-muted-foreground">
                    Share any specific details or focus areas
                  </p>
                </div>
              </div>

              {/* Suggested context from website */}
              {wizardData.suggestedContext && (
                <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Wand2 className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base">Programs found on your website</CardTitle>
                    </div>
                    <CardDescription>
                      Key initiatives and priorities we identified
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground bg-background/50 rounded-md p-3 border">
                        {wizardData.suggestedContext}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => updateField("additionalContext", wizardData.suggestedContext)}
                        className="h-7 text-xs"
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Use this context
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label htmlFor="additionalContext">Additional Notes (Optional)</Label>
                  {wizardData.suggestedContext && !wizardData.additionalContext && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => updateField("additionalContext", wizardData.suggestedContext)}
                      className="h-auto p-0 text-xs text-primary"
                    >
                      Paste from diocese website?
                    </Button>
                  )}
                </div>
                <Textarea
                  id="additionalContext"
                  placeholder="Any specific challenges, opportunities, or focus areas for your diocese? Recent events, upcoming anniversaries, or particular needs?"
                  value={wizardData.additionalContext}
                  onChange={(e) => updateField("additionalContext", e.target.value)}
                  className="min-h-[150px]"
                />
              </div>

              <Card className="bg-muted/30 border-dashed">
                <CardContent className="pt-4">
                  <div className="flex gap-3">
                    <Lightbulb className="h-5 w-5 text-primary flex-shrink-0" />
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p><strong>Tips for better results:</strong></p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Mention any ongoing initiatives you want to build upon</li>
                        <li>Note specific challenges facing your diocese</li>
                        <li>Include target demographics or regions of focus</li>
                        <li>Reference any recent pastoral letters or directives</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 5: Review & Generate */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Review & Generate</h3>
                  <p className="text-sm text-muted-foreground">
                    Confirm your selections and generate your plan
                  </p>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Diocese Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="grid grid-cols-2 gap-2 text-sm">
                      <dt className="text-muted-foreground">Name:</dt>
                      <dd className="font-medium">{wizardData.dioceseName || "—"}</dd>
                      <dt className="text-muted-foreground">Location:</dt>
                      <dd className="font-medium">
                        {[wizardData.city, wizardData.state].filter(Boolean).join(", ") || "—"}
                      </dd>
                      <dt className="text-muted-foreground">Bishop:</dt>
                      <dd className="font-medium">{wizardData.bishopName || "—"}</dd>
                      <dt className="text-muted-foreground">Size:</dt>
                      <dd className="font-medium capitalize">{wizardData.dioceseSize || "—"}</dd>
                    </dl>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Sections to Include
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(SECTION_LABELS)
                        .filter(([key]) => wizardData.sections[key as keyof WizardData["sections"]])
                        .map(([key, { title }]) => (
                          <Badge key={key} variant="secondary" className="text-xs">
                            {title}
                          </Badge>
                        ))}
                    </div>
                  </CardContent>
                </Card>

                {(wizardData.visionStatement || wizardData.missionStatement) && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        Vision & Mission
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      {wizardData.visionStatement && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Vision:</p>
                          <p className="text-foreground">{wizardData.visionStatement}</p>
                        </div>
                      )}
                      {wizardData.missionStatement && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Mission:</p>
                          <p className="text-foreground">{wizardData.missionStatement}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Generate Button */}
              <div className="pt-4">
                {isGenerating ? (
                  <Card className="border-primary/30 bg-primary/5">
                    <CardContent className="py-8">
                      <div className="flex flex-col items-center gap-4">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <div className="text-center">
                          <p className="font-medium text-foreground">Generating your plan...</p>
                          <p className="text-sm text-muted-foreground mt-1">{progressMessage}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Button 
                    onClick={handleGenerate} 
                    size="lg" 
                    className="w-full"
                  >
                    <Sparkles className="mr-2 h-5 w-5" />
                    Generate Leadership Plan
                  </Button>
                )}
              </div>
            </div>
          )}
        </ScrollArea>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t border-border mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(prev => prev - 1)}
            disabled={currentStep === 1 || isGenerating}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          {currentStep < 5 ? (
            <Button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceed()}
            >
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DiocesanPlannerWizard;
